import { useState } from "react";
import type {
  ProcedureCategory,
  ProcedureCode,
  ProcedureScope,
  ProcedureVisitInput,
  UpdateProcedureCode,
} from "@dentacore/shared-types";

import { Modal } from "../ui/modal";
import { NumberField, sanitiseNumeric } from "../ui/number-field";
import {
  draftsFromCode,
  editChanges,
  emptyDrafts,
  formatMinor,
  GAP,
  MAX_VISITS,
  MINUTES,
  parseVisitCount,
  PRICE,
  resizeDrafts,
  toVisitInputs,
  totalOf,
  visitsValid,
} from "./procedure-form";
import type { VisitDraft } from "./procedure-form";
import styles from "./procedures.module.css";
import { RequiredMark } from "../ui/required-mark";

/** What the dialog is for. Duplicate is Add, filled in from another procedure. */
export type ProcedureDialogMode =
  { kind: "add"; from: ProcedureCode | null } | { kind: "edit"; code: ProcedureCode };

export interface NewProcedure {
  code: string;
  label: string;
  /** Required (PG-D4). */
  procedureCategoryId: string;
  defaultDurationMinutes: number;
  defaultPriceCents: number;
  scope: ProcedureScope;
}

/**
 * *Add a procedure*, *Edit* and *Duplicate*, as one dialog with the same fields
 * (TR4). Both forms used to open inside the list — Add above it, Edit under its
 * row — and pushed every row below them down while they were filled in.
 *
 * **Done on is asked only when adding**, as it always was: once a procedure
 * exists it is changed on the spot in its panel. **Duplicate opens Add filled
 * in** (Anthony's QA, item 11) rather than writing a copy, because a copy would
 * need a name invented for it and the form puts the cursor where the difference
 * goes.
 *
 * **Bookings already made keep what they were booked with** (Anthony,
 * 2026-09-24): a course copies its procedure's visits when it is booked (PV3),
 * so nothing saved here reaches one.
 */
export function ProcedureDialog({
  mode,
  categories,
  currency,
  busy,
  onCreate,
  onEdit,
  onClose,
  location,
}: {
  /** Where a new procedure goes, when several locations are shown. */
  location?: React.ReactNode;
  mode: ProcedureDialogMode;
  /** The clinic's categories, in order (PG-D1). Null while being read. */
  categories: readonly ProcedureCategory[] | null;
  /** ISO 4217, from the clinic — never assumed to have two decimal places. */
  currency: string;
  busy: boolean;
  /**
   * Add one. `visits` is the whole list for a multi-visit procedure (PV1), set
   * straight after the row is created; null for one visit, which is most.
   * Resolves true once it landed, so the dialog closes only then.
   */
  onCreate: (body: NewProcedure, visits: ProcedureVisitInput[] | null) => Promise<boolean>;
  /** The row's changed fields, and the visits when they changed. */
  onEdit: (body: UpdateProcedureCode, visits: ProcedureVisitInput[] | null) => Promise<boolean>;
  onClose: () => void;
}): React.JSX.Element {
  const source = mode.kind === "edit" ? mode.code : mode.from;
  const [code, setCode] = useState(source?.code ?? "");
  const [label, setLabel] = useState(source?.label ?? "");
  /**
   * **Per tooth by default, because most dentistry is** — a composite, a crown
   * and a root canal are all done on a tooth, and whitening and a scale are the
   * exceptions somebody marks as they enter them.
   */
  const [scope, setScope] = useState<ProcedureScope>(source?.scope ?? "PER_TOOTH");
  /**
   * **Required, and not pre-picked on a new one** (PG-D4): a default would file
   * every hurried entry under whatever came first. Duplicate and Edit start on
   * the procedure's own.
   */
  const [categoryId, setCategoryId] = useState(source?.procedureCategoryId ?? "");
  const [drafts, setDrafts] = useState<VisitDraft[]>(() =>
    source === null ? emptyDrafts() : draftsFromCode(source),
  );

  const title = mode.kind === "edit" ? `Edit ${mode.code.label}` : "Add a procedure";
  const complete =
    code.trim() !== "" && label.trim() !== "" && categoryId !== "" && visitsValid(drafts);

  const edit = (index: number, change: Partial<VisitDraft>): void => {
    setDrafts((was) => was.map((row, at) => (at === index ? { ...row, ...change } : row)));
  };

  const save = (): void => {
    if (!complete) return;

    if (mode.kind === "edit") {
      const changes = editChanges(mode.code, code, label, drafts, categoryId);
      // Nothing changed is a closed dialog, not a request.
      if (changes === null) {
        onClose();
        return;
      }
      void onEdit(changes.body, changes.visits).then((landed) => {
        if (landed) onClose();
      });
      return;
    }

    void onCreate(
      {
        code: code.trim(),
        label: label.trim(),
        procedureCategoryId: categoryId,
        // The first visit's length and the visits' total — exactly what the
        // server makes the row once the visits are set (PV2), so a one-visit
        // procedure needs nothing more.
        defaultDurationMinutes: Number(drafts[0]?.minutes ?? 0),
        defaultPriceCents: totalOf(drafts),
        scope,
      },
      drafts.length > 1 ? toVisitInputs(drafts) : null,
    ).then((landed) => {
      if (landed) onClose();
    });
  };

  return (
    <Modal label={title} onClose={onClose} testId="proc-dialog" panelClassName={styles.dialog}>
      <form
        className={styles.dialogForm}
        onSubmit={(event) => {
          event.preventDefault();
          save();
        }}
      >
        <h2 className={styles.dialogTitle}>{title}</h2>
        {location}

        <div className={mode.kind === "edit" ? styles.namesEdit : styles.names}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>
              Code
              <RequiredMark />
            </span>
            <input
              className={styles.input}
              value={code}
              // Capitals, five characters at most (Emile's QA, 2026-10-18). An
              // older code already longer than five is shown whole and can only
              // be shortened — `maxLength` never trims a value it was handed.
              onChange={(event) => setCode(event.target.value.toUpperCase().slice(0, 5))}
              maxLength={5}
              autoComplete="off"
              autoCapitalize="characters"
              autoFocus
              data-testid="proc-code"
            />
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>
              Name
              <RequiredMark />
            </span>
            <input
              className={styles.input}
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              autoComplete="off"
              data-testid="proc-label"
            />
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>
              Category
              <RequiredMark />
            </span>
            <select
              className={styles.input}
              value={categoryId}
              onChange={(event) => setCategoryId(event.target.value)}
              disabled={categories === null}
              data-testid="proc-category"
            >
              <option value="" disabled>
                {categories === null ? "Reading…" : "Choose one…"}
              </option>
              {(categories ?? []).map((row) => (
                <option key={row.id} value={row.id}>
                  {row.name}
                </option>
              ))}
            </select>
          </label>
          {/*
            **This decides one thing** (TP12): whether a booking asks which
            tooth. It is not a clinical taxonomy and must not grow into one.
          */}
          {mode.kind === "add" && (
            <label className={styles.field}>
              <span className={styles.fieldLabel}>
                Done on
                <RequiredMark />
              </span>
              <select
                className={styles.input}
                value={scope}
                onChange={(event) => setScope(event.target.value as ProcedureScope)}
                data-testid="proc-scope"
              >
                <option value="PER_TOOTH">One tooth</option>
                <option value="WHOLE_MOUTH">The whole mouth</option>
              </select>
            </label>
          )}
        </div>

        <VisitsFields drafts={drafts} onDrafts={setDrafts} onEdit={edit} currency={currency} />

        <div className={styles.dialogActions}>
          <button type="button" className={styles.ghost} onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className={styles.primary}
            disabled={busy || !complete}
            data-testid="proc-save"
          >
            {mode.kind === "edit" ? "Save changes" : "Add it"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * How many visits, and what each one is (STORY-152).
 *
 * **One visit shows exactly the two fields it always did** — minutes and price —
 * because most procedures are one visit and the form for them should not change.
 * **Two or more** shows a row per visit — name, days after the one before,
 * minutes, price — and the total they come to, which is what the procedure's
 * price becomes (PV2).
 */
function VisitsFields({
  drafts,
  onDrafts,
  onEdit,
  currency,
}: {
  drafts: readonly VisitDraft[];
  onDrafts: (next: VisitDraft[]) => void;
  onEdit: (index: number, change: Partial<VisitDraft>) => void;
  currency: string;
}): React.JSX.Element {
  const count = drafts.length;
  /**
   * **What is typed in the count, kept apart from how many rows there are**
   * (TR5). Emile: *"the number of visits should be an integer I can input."* It
   * was a select for one reason — a half-typed `1` on the way to `12` would add
   * and drop eleven rows — so the rows follow only when the field is left or
   * Enter is pressed, never per keystroke.
   */
  const [countText, setCountText] = useState(String(count));
  const [countError, setCountError] = useState<string | null>(null);

  const commitCount = (): void => {
    const next = parseVisitCount(countText);
    if (next === null) {
      // Refused whole, and nothing changes: the rows stay, and so does their count.
      setCountError(`Visits is a whole number from 1 to ${String(MAX_VISITS)}.`);
      setCountText(String(count));
      return;
    }
    setCountError(null);
    setCountText(String(next));
    if (next !== count) onDrafts(resizeDrafts(drafts, next));
  };

  return (
    <div className={styles.visitsBlock} data-testid="proc-visits">
      <div className={styles.visitsHead}>
        <label className={`${styles.field} ${styles.countField}`}>
          <span className={styles.fieldLabel}>
            Visits
            <RequiredMark />
          </span>
          <input
            className={styles.input}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            value={countText}
            aria-invalid={countError !== null}
            onChange={(event) =>
              // The same filter as every number field (QA20-13): `2e` never lands.
              setCountText(sanitiseNumeric(event.target.value, { min: 1, integer: true }))
            }
            onBlur={commitCount}
            onKeyDown={(event) => {
              // Enter applies the count; it must not submit the half-filled form.
              if (event.key !== "Enter") return;
              event.preventDefault();
              commitCount();
            }}
            data-testid="proc-visit-count"
          />
        </label>
        {count > 1 && (
          <span className={styles.total} data-testid="visits-total">
            {count} visits · {formatMinor(totalOf(drafts), currency)} in all
          </span>
        )}
      </div>
      {countError !== null && (
        <p className={styles.error} role="alert" data-testid="proc-visit-count-error">
          {countError}
        </p>
      )}

      {count === 1 ? (
        <div className={styles.pair}>
          <NumberField
            label="Minutes"
            className={styles.field}
            labelClassName={styles.fieldLabel}
            inputClassName={styles.input}
            value={drafts[0]?.minutes ?? ""}
            onChange={(minutes) => onEdit(0, { minutes })}
            rules={MINUTES}
            required
            testId="proc-minutes"
          />
          <NumberField
            label={`Price (${currency})`}
            className={styles.field}
            labelClassName={styles.fieldLabel}
            inputClassName={styles.input}
            value={drafts[0]?.price ?? ""}
            onChange={(price) => onEdit(0, { price })}
            rules={PRICE}
            required
            testId="proc-price"
          />
        </div>
      ) : (
        <ol className={styles.visitRows}>
          {drafts.map((row, index) => (
            <li key={index} className={styles.visitRow} data-testid="visit-row">
              <span className={styles.visitNumber} aria-hidden="true">
                {index + 1}
              </span>
              <label className={`${styles.field} ${styles.visitName}`}>
                <span className={styles.fieldLabel}>Name</span>
                <input
                  className={styles.input}
                  value={row.title}
                  placeholder={`Visit ${String(index + 1)}`}
                  aria-label={`Visit ${String(index + 1)} name`}
                  onChange={(event) => onEdit(index, { title: event.target.value })}
                  data-testid="visit-title"
                />
              </label>
              {/*
                No gap on the first visit: the others are counted from it, and
                "days after the previous visit" has nothing to be after.
              */}
              {index === 0 ? (
                <span className={`${styles.field} ${styles.visitFirst}`}>
                  <span className={styles.fieldLabel} aria-hidden="true">
                    &nbsp;
                  </span>
                  <span className={styles.firstWord}>First visit</span>
                </span>
              ) : (
                <NumberField
                  label="Days later"
                  className={styles.field}
                  labelClassName={styles.fieldLabel}
                  inputClassName={styles.input}
                  value={row.gap}
                  onChange={(gap) => onEdit(index, { gap })}
                  rules={GAP}
                  required
                  testId="visit-gap"
                />
              )}
              <NumberField
                label="Minutes"
                className={styles.field}
                labelClassName={styles.fieldLabel}
                inputClassName={styles.input}
                value={row.minutes}
                onChange={(minutes) => onEdit(index, { minutes })}
                rules={MINUTES}
                required
                testId="visit-minutes"
              />
              <NumberField
                label={`Price (${currency})`}
                className={styles.field}
                labelClassName={styles.fieldLabel}
                inputClassName={styles.input}
                value={row.price}
                onChange={(price) => onEdit(index, { price })}
                rules={PRICE}
                required
                testId="visit-price"
              />
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
